import { useCurrentProduct } from "@/src/context/product/current-product-context"
import { AnswerFromCustomer } from "./wizard"

export interface ValidateResult { questionId?: string, errorMessage?: string, hasError: boolean }

export const useProductValidations = () => {
    const { setQuestionResultError, clearQuestionResultError, answers, currentProduct } = useCurrentProduct()

    const getFormattedWizardAnswer = (answerFromCustomer: AnswerFromCustomer) => {
        const { isRequired, minQtyAnswer, maxQtyAnswer } = answerFromCustomer

        if (isRequired && minQtyAnswer < 1) return 'Escolha uma opção'

        if (minQtyAnswer === maxQtyAnswer)
            return `Escolha ${maxQtyAnswer} ${maxQtyAnswer > 1 ? 'itens' : 'item'}`

        if (maxQtyAnswer < minQtyAnswer)
            return `Escolha no mínimo ${minQtyAnswer} ${minQtyAnswer > 1 ? 'itens' : 'item'}`

        return `Escolha entre ${minQtyAnswer} e ${maxQtyAnswer} itens`
    }

    const validateWizard = (): ValidateResult => {
        clearQuestionResultError()
        let validationResult: ValidateResult = { hasError: false }

        // Caso tentar adicionar sem selecionar nenhuma resposta.
        if (answers.length < 1 && (currentProduct?.itemGroups && currentProduct?.itemGroups?.length > 0)) {
            const firstRequired = currentProduct?.itemGroups.filter(group => group.isRequired)[0]

            if (firstRequired) {
                addNewValidationResultError(firstRequired.id)
                validationResult.hasError = true
                return validationResult
            }
        }

        const unansweredRequiredGroups = currentProduct?.itemGroups?.filter(group =>
            group.isRequired && !answers.some(answer => answer.id === group.id)
        )

        if (unansweredRequiredGroups && unansweredRequiredGroups.length > 0) {
            const errorMessage = getFormattedWizardAnswer(unansweredRequiredGroups[0] as AnswerFromCustomer)

            addNewValidationResultError(unansweredRequiredGroups[0].id)
            validationResult = { hasError: true, errorMessage }
        }

        // Validação de respostas específicas
        for (const currentWizard of answers) {
            const validateResult = validateSingleAnswer(currentWizard)
            if (validateResult.hasError) {
                addNewValidationResultError(currentWizard.id)
                validationResult = validateResult
            }
        }

        return validationResult
    }

    const validateSingleAnswer = (answerFromCustomer: AnswerFromCustomer): ValidateResult => {

        const selectedItemsCount = answerFromCustomer.itemOptionsGroup.length

        if (selectedItemsCount < answerFromCustomer.minQtyAnswer) {
            return { hasError: true, errorMessage: getFormattedWizardAnswer(answerFromCustomer) }
        }

        return { hasError: false }
    }

    const scrollToQuestion = (questionId: string, nextQuestion: boolean) => {
        const selected = document.getElementById(questionId)

        if (selected) {
            const selectedOrdering = selected.getElementsByClassName('scroll-referrer')[0].getAttribute('data-ordering')

            if (selectedOrdering) {
                const ordering = Number.parseInt(selectedOrdering)

                const question = document.querySelector(`[data-ordering="${nextQuestion ? ordering + 1 : ordering}"]`);

                if (question) {
                    question.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
                }
            }
        }
    }

    const addNewValidationResultError = (questionsIdWithError: string) => {
        if (questionsIdWithError.length > 0) {
            const groupEl = document?.getElementById(questionsIdWithError)

            if (groupEl)
                scrollToQuestion(questionsIdWithError, false)

            const subtitle = document.getElementById(`sub-${questionsIdWithError}`)

            if (subtitle) {
                subtitle.style.color = 'red'
                subtitle.style.fontWeight = '600'
                subtitle.textContent = `*${subtitle.textContent}`.split('*')
                    .reduce((prev, curr, i) => prev + (i == 1 ? '*' : '') + curr)
            }

            setQuestionResultError(questionsIdWithError)
        }
    }

    const scrollToNextQuestion = (questionid: string, _answers: AnswerFromCustomer[]) => {
        const selectedAnswer = _answers.find((c) => c.id === questionid)

        const canProceed = selectedAnswer
            && ((selectedAnswer.itemOptionsGroup.length >= selectedAnswer.maxQtyAnswer) || (!selectedAnswer?.maxQtyAnswer && selectedAnswer.isRequired))

        if (canProceed)
            scrollToQuestion(selectedAnswer.id, true)
    }

    return { validateWizard, scrollToNextQuestion }
}